"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { Link } from "react-router-dom"
import {
  Search,
  MapPin,
  Briefcase,
  Filter,
  Star,
  Sparkles,
  Calendar,
  IndianRupee,
  X,
  SlidersHorizontal,
} from "lucide-react"
import { CircleLoader } from "react-spinners"
import { tamilNaduSubdistricts } from "../utils/tamilNaduSubdistricts"

const JobBoardComponent = ({ job, handleTagClick }) => {
  const {
    _id,
    company,
    logo,
    featured,
    position,
    district,
    salary,
    experience,
    contract,
    shortDescription,
    createdAt
  } = job

  const isNew = () => {
    if (!createdAt) return false
    const jobDate = new Date(createdAt)
    const now = new Date()
    const diffInHours = (now - jobDate) / (1000 * 60 * 60)
    return diffInHours <= 24
  }

  const truncateChars = (text, limit) => {
    if (!text) return ''
    return text.length > limit ? text.slice(0, limit) + '...' : text
  }

  return (
    <Link
      to={`/jd/${_id}`}
      className="block bg-white border border-gray-100 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1 flex flex-col h-[220px] overflow-hidden pb-2"
    >
      {/* Card Header with Company Info */}
      <div className="p-6 pb-4 flex items-center gap-4 border-b border-gray-100 bg-gradient-to-br from-white to-gray-50">
        <div className="w-16 h-16 rounded-xl border border-gray-200 flex items-center justify-center bg-white shadow-sm flex-shrink-0 overflow-hidden">
          <img
            className="w-full h-full object-contain p-1"
            src={logo || "/placeholder.svg?height=64&width=64&query=company logo"}
            alt={company}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-gray-600 truncate">{company}</h4>
            <div className="flex gap-1.5 flex-shrink-0">
              {isNew() && (
                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-0.5 rounded-full animate-pulse">
                  <Sparkles className="w-3 h-3" /> New
                </span>
              )}
              {featured && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                  <Star className="w-3 h-3" /> Featured
                </span>
              )}
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-tight line-clamp-2">{position}</h3>
        </div>
      </div>

      {/* Job Details Section */}
      <div className="p-6 pt-4 flex-grow flex flex-col">
        <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="truncate">{district}</span>
          </div>
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="truncate">{salary || "Not disclosed"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="truncate">{experience || "0-2 years"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="truncate">{contract}</span>
          </div>
        </div>

        {/* Truncated Description snippet */}
        {shortDescription && (
          <p className="text-xs text-gray-600 mb-3">
            {truncateChars(shortDescription, 25)}
          </p>
        )}
      </div>
    </Link>
  )
}

// Filter Sidebar Component
const FilterSidebar = ({
  isOpen,
  onClose,
  filter,
  locationFilter,
  levelFilter,
  dateFilter,
  handleFilterChange,
  handleLocationChange,
  handleLevelChange,
  handleDateChange,
  clearFilters,
  tamilNaduDistricts,
  filteredJobsCount,
}) => {
  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={onClose}></div>}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-72 sm:w-80 border-r border-gray-200 z-30 transform transition-transform duration-300 ease-in-out overflow-y-auto ${isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 lg:bg-transparent bg-white`}
      >
        <div className="p-4 sm:p-6 mt-16 sm:mt-20">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Filters</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-500">({filteredJobsCount} jobs)</span>
              <button onClick={onClose} className="lg:hidden p-1 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Clear All Button */}
          <button
            onClick={clearFilters}
            className="w-full mb-4 sm:mb-6 px-3 py-2 text-xs sm:text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Clear all filters
          </button>

          {/* Filter Options */}
          <div className="space-y-4 sm:space-y-6">
            {/* Job Type Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">Job Type</label>
              <div className="space-y-1.5 sm:space-y-2">
                {["All", "Full-time", "Part-time", "Internship"].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="jobType"
                      value={option}
                      checked={filter === option}
                      onChange={(e) => handleFilterChange({ target: { value: e.target.value } })}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-xs sm:text-sm text-gray-700">
                      {option === "All" ? "All Job Types" : option}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">Location</label>
              <select
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm bg-white"
                value={locationFilter}
                onChange={handleLocationChange}
              >
                <option value="All">All Locations</option>
                {tamilNaduDistricts.map((district, i) => (
                  <option key={i} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            {/* Experience Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                Experience Level
              </label>
              <div className="space-y-1.5 sm:space-y-2">
                {["All", "Junior", "Mid", "Senior"].map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="experience"
                      value={option}
                      checked={levelFilter === option}
                      onChange={(e) => handleLevelChange({ target: { value: e.target.value } })}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-xs sm:text-sm text-gray-700">
                      {option === "All" ? "All Experience" : option === "Mid" ? "Mid Level" : option}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date Posted Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">Date Posted</label>
              <div className="space-y-1.5 sm:space-y-2">
                {[
                  { value: "All", label: "Any Time" },
                  { value: "24h", label: "Last 24 Hours" },
                  { value: "7d", label: "Last 7 Days" },
                  { value: "30d", label: "Last 30 Days" },
                ].map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      name="datePosted"
                      value={option.value}
                      checked={dateFilter === option.value}
                      onChange={(e) => handleDateChange({ target: { value: e.target.value } })}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-xs sm:text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const Job = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("All")
  const [locationFilter, setLocationFilter] = useState("All")
  const [levelFilter, setLevelFilter] = useState("All")
  const [dateFilter, setDateFilter] = useState("All")
  const [selectedTags, setSelectedTags] = useState([])
  const [allJobs, setAllJobs] = useState([])
  const [filteredJobs, setFilteredJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs`)
        setAllJobs(response.data)
        setFilteredJobs(response.data)
      } catch (error) {
        console.error("Error fetching jobs:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchJobs()
  }, [])

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase()
    setSearchTerm(term)
    filterJobs(term, filter, locationFilter, levelFilter, dateFilter, selectedTags)
  }

  const handleFilterChange = (e) => {
    const selectedFilter = e.target.value
    setFilter(selectedFilter)
    filterJobs(searchTerm, selectedFilter, locationFilter, levelFilter, dateFilter, selectedTags)
  }

  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value
    setLocationFilter(selectedLocation)
    filterJobs(searchTerm, filter, selectedLocation, levelFilter, dateFilter, selectedTags)
  }

  const handleLevelChange = (e) => {
    const selectedLevel = e.target.value
    setLevelFilter(selectedLevel)
    filterJobs(searchTerm, filter, locationFilter, selectedLevel, dateFilter, selectedTags)
  }

  const handleDateChange = (e) => {
    const selectedDate = e.target.value
    setDateFilter(selectedDate)
    filterJobs(searchTerm, filter, locationFilter, levelFilter, selectedDate, selectedTags)
  }

  const handleTagClick = (tag) => {
    const updatedTags = selectedTags.includes(tag) ? selectedTags.filter((t) => t !== tag) : [...selectedTags, tag]
    setSelectedTags(updatedTags)
    filterJobs(searchTerm, filter, locationFilter, levelFilter, dateFilter, updatedTags)
  }

  const filterJobs = (term, selectedFilter, location, level, date, tags) => {
    let filtered = allJobs.filter((job) => job.position?.toLowerCase().includes(term))
    if (selectedFilter !== "All") filtered = filtered.filter((job) => job.contract === selectedFilter)
    if (location !== "All") filtered = filtered.filter((job) => job.district === location)
    if (level !== "All") filtered = filtered.filter((job) => job.level === level)
    if (date !== "All") {
      const currentDate = new Date()
      let dateThreshold
      switch (date) {
        case "24h":
          dateThreshold = new Date(currentDate.setDate(currentDate.getDate() - 1))
          break
        case "7d":
          dateThreshold = new Date(currentDate.setDate(currentDate.getDate() - 7))
          break
        case "30d":
          dateThreshold = new Date(currentDate.setDate(currentDate.getDate() - 30))
          break
        default:
          break
      }
      filtered = filtered.filter((job) => new Date(job.createdAt) >= dateThreshold)
    }
    if (tags.length > 0) {
      filtered = filtered.filter((job) =>
        tags.every((tag) =>
          [
            job.role,
            job.level,
            job.salary,
            job.experience,
            job.createdAt,  // Updated from dateOfJoining to createdAt
            ...(job.languages || []),
            ...(job.tools || []),
          ].includes(tag),
        ),
      )
    }
    setFilteredJobs(filtered)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setFilter("All")
    setLocationFilter("All")
    setLevelFilter("All")
    setDateFilter("All")
    setSelectedTags([])
    setFilteredJobs(allJobs)
  }

  const tamilNaduDistricts = Object.keys(tamilNaduSubdistricts)

  return (
    <div className="">
      {/* Main Layout */}
      <div className="relative flex">
        {/* Filter Sidebar */}
        <FilterSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          filter={filter}
          locationFilter={locationFilter}
          levelFilter={levelFilter}
          dateFilter={dateFilter}
          handleFilterChange={handleFilterChange}
          handleLocationChange={handleLocationChange}
          handleLevelChange={handleLevelChange}
          handleDateChange={handleDateChange}
          clearFilters={clearFilters}
          tamilNaduDistricts={tamilNaduDistricts}
          filteredJobsCount={filteredJobs.length}
        />

        {/* Main Content */}
        <div className="w-full md:container lg:ml-72 xl:ml-80 transition-all duration-300">
          <div className=" max-w-none px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
            {/* Filter Toggle and Results Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Mobile Filter Button Only */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filters</span>
                </button>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{filteredJobs.length}</span> jobs found
                </div>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <CircleLoader size={48} color="#2563eb" />
              </div>
            ) : filteredJobs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 w-full">
                {filteredJobs.map((job) => (
                  <JobBoardComponent key={job._id} job={job} handleTagClick={handleTagClick} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-16 bg-white rounded-lg">
                <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
                  Try adjusting your search criteria or filters to find more opportunities.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Job
